/**
 * AppRouter — Renders all application routes from the centralized config.
 *
 * Eliminates boilerplate by mapping route configs through guard wrappers
 * (AuthGuard + Layout for protected routes, AdminGuard for admin routes).
 */

import type { ReactNode } from 'react';
import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '@shared/context/AuthContext';
import { Layout } from '@shared/components/Layout';
import { AuthGuard, PublicGuard } from '@features/authentication/components/AuthGuard';
import { AdminGuard } from '@features/authentication/components/AdminGuard';
import { LoginPage } from '@features/authentication/pages/LoginPage';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { adminRoutes, protectedRoutes } from './routes';

/**
 * Invisible component that initializes analytics tracking inside the Router context.
 * Tracks session lifecycle, page views, and provides analytics context.
 */
const AnalyticsTracker: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  useAnalytics({ isAuthenticated });
  return <>{children}</>;
};

/**
 * Moves focus to the main content area on every route change so screen reader
 * users are notified that the page has updated.
 */
const FocusManager: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    const mainEl = document.getElementById('main-content');
    if (mainEl) {
      mainEl.focus();
    }
  }, [location.pathname]);
  return null;
};

/**
 * AppRouter — Top-level routing component.
 * Must be rendered inside AuthProvider and LanguageProvider.
 */
export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <FocusManager />
      <AnalyticsTracker>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicGuard>
                <LoginPage />
              </PublicGuard>
            }
          />
          <Route
            path="/register"
            element={
              <PublicGuard>
                <LoginPage />
              </PublicGuard>
            }
          />

          {/* Root redirect */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <Layout>
                  <Navigate to="/dashboard" replace />
                </Layout>
              </AuthGuard>
            }
          />

          {/* Protected Routes — AuthGuard + Layout */}
          {protectedRoutes.map(({ path, component: Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <AuthGuard>
                  <Layout>
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Component />
                    </React.Suspense>
                  </Layout>
                </AuthGuard>
              }
            />
          ))}

          {/* Admin Routes — AuthGuard + AdminGuard, no Layout */}
          {adminRoutes.map(({ path, component: Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <AuthGuard>
                  <AdminGuard>
                    <React.Suspense fallback={<LoadingSpinner />}>
                      <Component />
                    </React.Suspense>
                  </AdminGuard>
                </AuthGuard>
              }
            />
          ))}

          {/* Catch all — redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AnalyticsTracker>
    </BrowserRouter>
  );
};
